// formatShortDate — single source of truth for the "15 Jun" / "15 Jun 2026"
// short-form date string used across contract surfaces (deliverable due
// dates, milestone schedules, meeting dates, SOW periods, variation
// effective dates, audit footer, activity log, term progress bar, etc.).
//
// Why centralise: `Intl.DateTimeFormat('en-AU', { month: 'short' })`
// returns "June" / "July" / "Sept" — a mix of 3-, 4- and 5-letter
// abbreviations — because the en-AU locale data treats those months
// specially. The brand spec calls for `MMM` (3-letter) consistently
// across the app. Routing every call through this helper guarantees a
// stable abbreviation regardless of the runtime locale.
//
// D3 wire contract: toUtcDate / formatDayMonthYear accept both YYYY-MM-DD
// (date-only, e.g. dueDate from form payloads) and ISO 8601 timestamps
// (e.g. submittedAt / acceptedAt / rejectedAt stamped server-side via
// new Date().toISOString()). The string is sliced to its date portion
// before parsing so calendar-day semantics survive non-UTC offsets —
// "2025-12-25T01:00:00+10:00" renders as 25 Dec 2025, not 24 Dec 2025.
// A round-trip check (parse → format back → assert equality) rejects
// impossible dates like "2025-02-30" engine-independently, because engines
// vary on whether they normalise or invalidate out-of-range values.
//
// formatDayMonthYearLocal is semantically distinct: it keys to the LOCAL
// calendar day of an instant (createdAt, occurredAt) and must not be
// collapsed into formatDayMonthYear. See its docstring below.

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Inputs are widened to null/undefined because stored rows can predate
// fields the wire types declare required (e.g. seeded e2e contracts without
// dates) — a formatter throwing on a sparse row takes the whole page down
// via the route error boundary. Nullish input renders as the empty string.
type DateInput = string | Date | null | undefined;

function toUtcDate(input: DateInput): Date | null {
  if (input == null) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  // Strip the time portion from ISO 8601 timestamps before constructing the
  // Date so that calendar-day semantics survive non-UTC offsets. A plain
  // YYYY-MM-DD string is used as-is.
  const dateOnly = input.includes("T") ? input.slice(0, 10) : input;
  if (!DATE_ONLY_RE.test(dateOnly)) return null;
  const d = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  // Round-trip check: engines may normalise impossible dates (e.g.
  // "2025-02-30" → 2025-03-02) rather than returning Invalid Date. Comparing
  // the formatted output back to the input slice catches both cases.
  if (d.toISOString().slice(0, 10) !== dateOnly) return null;
  return d;
}

/** "15 Jun" — short-form day + 3-letter month abbreviation. */
export function formatDayMonth(input: DateInput): string {
  const d = toUtcDate(input);
  if (!d) return typeof input === "string" ? input : "";
  return `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]}`;
}

/** "15 Jun 2026" — full short-form date used for detail views, card
 *  headers, and prose where the year matters. */
export function formatDayMonthYear(input: DateInput): string {
  const d = toUtcDate(input);
  if (!d) return typeof input === "string" ? input : "";
  return `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Jun 2026" — month + year for period labels (recurring deliverable
 *  parents, milestone summary timelines, etc.). */
export function formatMonthYear(input: DateInput): string {
  const d = toUtcDate(input);
  if (!d) return typeof input === "string" ? input : "";
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Jun 26" — month + two-digit year for chart axis ticks, where the full
 *  year costs width that twelve columns cannot spare. Use this rather than
 *  the bare month whenever the series can span a year boundary or repeat a
 *  month, which is both ambiguous read as "Jun" twice. */
export function formatMonthYearShort(input: DateInput): string {
  const d = toUtcDate(input);
  if (!d) return typeof input === "string" ? input : "";
  const year = String(d.getUTCFullYear()).slice(-2);
  return `${MONTH_ABBR[d.getUTCMonth()]} ${year}`;
}

/** "Jun" — bare 3-letter abbreviation for tick labels on charts. */
export function formatMonth(input: DateInput): string {
  const d = toUtcDate(input);
  if (!d) return typeof input === "string" ? input : "";
  return MONTH_ABBR[d.getUTCMonth()];
}

/** "15 Jun 2026" for an instant, keyed to its LOCAL calendar day.
 *  Use for timestamps (createdAt, occurredAt) where the displayed day
 *  must match the day the user observed the event in their own
 *  timezone — unlike formatDayMonthYear, which treats its input as a
 *  UTC calendar-day value. Empty/invalid input → "". */
export function formatDayMonthYearLocal(instant: DateInput): string {
  // Nullish must be caught before the Date constructor: new Date(null)
  // coerces null to 0 and would render the epoch as "1 Jan 1970".
  if (instant == null) return "";
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
}
